import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import { Alert, Avatar, Box, Button, Chip, CircularProgress, Container, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import MuxPlayer from '@mux/mux-player-react';
import { useAuth } from '../../context/AuthContext';
import server from '../../global/server';

export default () => {
    const { schedule_id } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [polling, setPolling] = useState(false);

    const fetchPlayback = useCallback(async () => {
        try {
            const { data: res } = await server.get(`/search/live/${schedule_id}`);
            if (res?.data?.playback_ids) {
                setData(res.data);
                setPolling(false);
                setError(null);
            } else {
                // Tem dados mas sem playback ainda
                setData(res.data);
                setPolling(true);
            }
        } catch (err) {
            const msg = err?.response?.data?.message || '';
            if (msg === 'Transmissao ainda nao iniciada') {
                // Esperado — manter polling
                // Tentar pegar os dados do encontro mesmo sem playback
                setPolling(true);
            } else {
                setError(msg || 'Erro ao carregar transmissão');
                setPolling(false);
            }
        } finally {
            setLoading(false);
        }
    }, [schedule_id]);

    // Primeira chamada
    useEffect(() => {
        fetchPlayback();
    }, [fetchPlayback]);

    // Polling a cada 10s enquanto não tem playback
    useEffect(() => {
        if (!polling) return;
        const interval = setInterval(fetchPlayback, 10000);
        return () => clearInterval(interval);
    }, [polling, fetchPlayback]);

    const playbackReady = data?.playback_ids;
    const meeting = data?.meeting;
    const host = meeting?.Profile?.User;

    // Tela de erro permanente
    if (!loading && error && !polling) {
        return (
            <Container maxWidth="sm" sx={{ py: 6, textAlign: 'center' }}>
                <Alert severity="warning" sx={{ borderRadius: '12px' }}>
                    {error}
                </Alert>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/lista/ao-vivo')}
                    sx={{ mt: 2, textTransform: 'none', color: '#666' }}
                >
                    Voltar
                </Button>
            </Container>
        );
    }

    // Tela de aguardando transmissão (polling ativo)
    if (!playbackReady) {
        return (
            <Box sx={{ bgcolor: '#111', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <Box sx={{ bgcolor: '#1a1a1a', borderBottom: '1px solid #333', px: 2, py: 1.5 }}>
                    <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate('/lista/ao-vivo')}
                            sx={{ textTransform: 'none', color: '#aaa', '&:hover': { color: 'white' } }}
                        >
                            Sair
                        </Button>
                        {meeting && (
                            <Typography variant="body2" sx={{ color: '#aaa' }} noWrap>
                                {meeting.title}
                            </Typography>
                        )}
                    </Container>
                </Box>

                {/* Aguardando */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, p: 4 }}>
                    <HourglassEmptyIcon sx={{ fontSize: 56, color: '#555' }} />
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, textAlign: 'center' }}>
                        Aguardando transmissão
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#aaa', textAlign: 'center', maxWidth: 400 }}>
                        O encontro ainda não começou ou a transmissão está sendo preparada.
                        Você entrará automaticamente quando estiver disponível.
                    </Typography>
                    <CircularProgress size={24} sx={{ color: '#006a61' }} />
                </Box>
            </Box>
        );
    }

    // Transmissão pronta — mostrar player
    return (
        <Box sx={{ bgcolor: '#111', minHeight: '100vh', pb: 4 }}>
            {/* Header */}
            <Box sx={{ bgcolor: '#1a1a1a', borderBottom: '1px solid #333', px: 2, py: 1.5 }}>
                <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate('/lista/ao-vivo')}
                            sx={{ textTransform: 'none', color: '#aaa', minWidth: 'auto', '&:hover': { color: 'white' } }}
                        >
                            Sair
                        </Button>
                        <Box sx={{ height: 20, width: '1px', bgcolor: '#444' }} />
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Avatar src={host?.profileImage} sx={{ width: 28, height: 28 }}>
                                {host?.name?.[0]}
                            </Avatar>
                            <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }} noWrap>
                                {meeting?.title}
                            </Typography>
                        </Stack>
                    </Stack>
                    <Chip
                        icon={<FiberManualRecordIcon sx={{
                            fontSize: 8,
                            color: '#ff4444 !important',
                            animation: 'pulse 1.5s infinite',
                            '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
                        }} />}
                        label="AO VIVO"
                        size="small"
                        sx={{ bgcolor: 'rgba(255,68,68,0.15)', color: '#ff6666', fontWeight: 600, fontSize: '0.7rem' }}
                    />
                </Container>
            </Box>

            {/* Player */}
            <Container maxWidth="lg" sx={{ mt: 2 }}>
                <MuxPlayer
                    autoPlay
                    title={meeting?.title}
                    streamType="live"
                    playbackId={data.playback_ids}
                    metadata={{
                        video_id: String(schedule_id),
                        video_title: meeting?.title,
                        viewer_user_id: String(user?.id),
                    }}
                    style={{ width: '100%', height: 'auto', borderRadius: '12px', overflow: 'hidden' }}
                />

                {/* Info abaixo do player */}
                <Box sx={{ mt: 2, px: 1 }}>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                        {meeting?.title}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 1 }}>
                        <Avatar src={host?.profileImage} sx={{ width: 36, height: 36 }}>
                            {host?.name?.[0]}
                        </Avatar>
                        <Box>
                            <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                                {host?.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#aaa' }}>
                                @{host?.username}
                            </Typography>
                        </Box>
                    </Stack>
                    {(meeting?.topic || meeting?.subtopic) && (
                        <Stack direction="row" spacing={0.75} sx={{ mt: 1.5 }}>
                            {meeting.topic && (
                                <Chip label={meeting.topic} size="small" sx={{ bgcolor: 'rgba(0,106,97,0.2)', color: '#4db6ac', fontSize: '0.7rem', height: 22 }} />
                            )}
                            {meeting.subtopic && (
                                <Chip label={meeting.subtopic} size="small" variant="outlined" sx={{ borderColor: '#555', color: '#aaa', fontSize: '0.7rem', height: 22 }} />
                            )}
                        </Stack>
                    )}
                </Box>
            </Container>
        </Box>
    );
};
